import { ApiEsignMarkupStatus } from "@/types/document.types.ts";
import { ApiEmployee } from "@/types/employees/employee.types.ts";
import { ReminderMode, ReminderPeriod } from "@/types/reminder.types.ts";
import { RuleGroupShort } from "@/types/rule.types.ts";

export enum EsignConditionType {
  affected_employee = "affected_employee",
  affected_employee_direct_manager = "affected_employee_direct_manager",
  employee = "employee",
  affected_employee_custom_field_employee_reference = "affected_employee_custom_field_employee_reference",
}

export enum EsignFieldType {
  signature = "signature",
  full_name = "full_name",
  initials = "initials",
  job_title = "job_title",
  job_role = "job_role",
  job_level = "job_level",
  current_date = "current_date",
  text = "text",
  yes_no = "yes_no",
}

export enum EsignFieldFilledBy {
  auto = "auto",
  sender = "sender",
  signer = "signer",
}

export interface ApiEsignMarkupField {
  id: number | null;
  field_type: EsignFieldType;
  page: number;
  pos_x: number;
  pos_y: number;
  width: number;
  height: number;
  font_size: number;
  filled_by: EsignFieldFilledBy;
  label: string | null;
  is_required: boolean;
  is_multiline: boolean;
}

export interface ApiEsignConditionEntity {
  id: number | string;
  title: string;
}

export interface ApiEsignMarkupRole {
  id: number | null;
  condition_type: EsignConditionType;
  condition_value: Array<number | string>;
  condition_entities?: ApiEsignConditionEntity[];
  fields: ApiEsignMarkupField[];
}

export interface ApiEsignMarkup {
  id: number;
  datetime_created_at: Date;
  datetime_updated_at: Date;
  document_id: number;
  document_title: string;
  status: ApiEsignMarkupStatus;
  can_edit: boolean;
  roles: ApiEsignMarkupRole[];
}

export interface ApiEsignMarkupSave {
  roles: ApiEsignMarkupRole[];
}

export enum EsignSignatureType {
  signature = "signature",
  acknowledge = "acknowledge",
}

export enum EsignEnvelopeStatus {
  pending = "pending",
  completed = "completed",
  cancelled = "cancelled",
}

export enum EsignRequestStatus {
  pending = "pending",
  signed = "signed",
  cancelled = "cancelled",
  declined = "declined",
}

export interface ApiEsignDocumentFilter {
  page_number?: number;
  author_ids?: number[] | null;
  status?: EsignEnvelopeStatus | null;
  search?: string | null;
}

export interface ApiEsignEnvelopeFilter extends ApiEsignDocumentFilter {
  document_id?: number | null;
}

export interface ApiEsignDocumentSummary {
  document_id: number;
  title: string;
  datetime_last_sent_at: Date;
  completed_envelopes_count: number;
  active_envelopes_count: number;
  total_envelopes_count: number;
}

export interface ApiEsignDocumentCounts {
  created_by_me: number;
  in_progress: number;
  completed: number;
  all: number;
}

export interface ApiEsignEnvelopeSigner {
  employee: ApiEsignSigner | null;
  status: EsignRequestStatus;
}

export interface ApiEsignDocumentEnvelope {
  public_id: string;
  status: EsignEnvelopeStatus;
  signature_type: EsignSignatureType;
  date_due: Date | null;
  datetime_created_at: Date;
  datetime_completed_at: Date | null;
  signed_requests_count: number;
  active_requests_count: number;
  total_requests_count: number;
  signers: ApiEsignEnvelopeSigner[];
}

export interface ApiEsignSigner {
  id: number;
  first_name: string;
  last_name: string;
  avatar_url: string | null;
  avatar_uuid: string | null;
}

export interface ApiEsignRequest {
  public_id: string;
  signer: ApiEsignSigner | null;
  status: EsignRequestStatus;
  signature_type: EsignSignatureType;
  datetime_signed_at: Date | null;
  date_due: Date | null;
  document_id: number;
  document_title: string;
  signed_document_id: number | null;
  signed_document_title: string | null;
  identity_verification_required: boolean;
  datetime_declined_at: Date | null;
  decline_reason: string | null;
  intent_statement: string;
}

export interface ApiEsignResultSigner {
  request_public_id: string;
  employee_id: number | null;
  full_name: string;
  avatar_url: string | null;
  status: EsignRequestStatus;
  datetime_signed_at: Date | null;
}

export interface ApiEsignResult {
  envelope_public_id: string;
  signature_type: EsignSignatureType;
  status: EsignEnvelopeStatus;
  date_due: Date | null;
  datetime_created_at: Date;
  datetime_completed_at: Date | null;
  signers: ApiEsignResultSigner[];
}

export interface ApiEsignEnvelope {
  public_id: string;
  document_id: number;
  document_title: string;
  signed_document_id: number | null;
  signed_document_title: string | null;
  status: EsignEnvelopeStatus;
  date_due: Date | null;
  signature_type: EsignSignatureType;
  created_by: ApiEmployee;
  datetime_completed_at: Date | null;
  requests: ApiEsignRequest[];
}

export interface ApiEsignEnvelopeCreationJob {
  id: number;
  status: string;
  envelope_ids: number[];
  error_code: string | null;
  error_message: string | null;
  datetime_created_at: Date;
  datetime_finished_at: Date | null;
}

export interface ApiEsignEnvelopePreviewRole {
  role_id: number;
  condition_type: EsignConditionType;
  recipients_count: number;
  employee_ids: number[];
}

export interface ApiEsignEnvelopePreviewWarning {
  role_id: number;
  condition_type: EsignConditionType;
  affected_employee: ApiEsignSigner;
}

export interface ApiEsignEnvelopePreview {
  envelopes_count: number;
  roles: ApiEsignEnvelopePreviewRole[];
  warnings: ApiEsignEnvelopePreviewWarning[];
}

export interface ApiEsignSenderFieldValue {
  field_id: number;
  value: string;
}

export interface ApiEsignEnvelopeSave {
  document_id: number;
  target_folder_id: number;
  signature_type: EsignSignatureType;
  date_due?: Date | null;
  recipient_rule_groups: RuleGroupShort[];
  reminder_mode?: ReminderMode | null;
  reminder_custom_frequency?: number | null;
  reminder_custom_period?: ReminderPeriod | null;
  field_values?: ApiEsignSenderFieldValue[];
  replace_source_document?: boolean;
}

export interface ApiEsignEnvelopePreviewFilter {
  document_id: number;
  recipient_rule_groups?: RuleGroupShort[];
}

export interface ApiEsignOtpVerify {
  code: string;
  consent_version: string;
}

export interface ApiEsignOtpRequested {
  email: string;
  consent_version: string;
  consent_text: string;
}

export interface ApiEsignRequestDecline {
  reason: string | null;
  withdraw_consent: boolean;
}

export interface ApiEsignField {
  field_type: EsignFieldType;
  page: number;
  pos_x: number;
  pos_y: number;
  width: number;
  height: number;
  font_size: number;
  value: string | null;
  field_key: string;
  filled_by: EsignFieldFilledBy;
  label: string | null;
  is_required: boolean;
  is_multiline: boolean;
}

export interface ApiEsignFieldValue {
  field_key: string;
  value: string;
}

export interface ApiEsignSignRequest {
  field_values: ApiEsignFieldValue[];
}

export interface ApiEsignSigningContextRole {
  is_current_role: boolean;
  fields: ApiEsignField[];
}

export interface ApiEsignSigningContext {
  request: ApiEsignRequest;
  source_sha256: string;
  roles: ApiEsignSigningContextRole[];
}

export interface ApiEsignEvidenceEnvelope {
  public_id: string;
  document_id: number;
  document_title: string;
  signed_document_id: number | null;
  signed_document_title: string | null;
  status: string;
  date_due: Date | null;
  signature_type: EsignSignatureType;
  created_by: ApiEmployee;
  datetime_completed_at: Date | null;
  source_file_uuid: string | null;
  source_sha256: string | null;
  signed_file_uuid: string | null;
  signed_sha256: string | null;
}

export interface ApiEsignLogChainVerification {
  is_valid: boolean;
  total_entries: number;
  broken_at_log_id: number | null;
  reason: string | null;
}

export interface ApiEsignIdentityVerification {
  method: string;
  identity_type: string;
  identity_value: string;
  datetime_verified_at: Date;
  datetime_valid_to: Date;
  datetime_tsa_time: Date;
}

export interface ApiEsignEvidenceSigner {
  request_public_id: string;
  signer: ApiEmployee;
  status: EsignRequestStatus;
  datetime_signed_at: Date | null;
  ip: string | null;
  user_agent: string | null;
  auth_method: string;
  identity_verification: ApiEsignIdentityVerification | null;
}

export enum EsignEvidenceEventType {
  envelope_created = "envelope_created",
  request_signed = "request_signed",
  envelope_completed = "envelope_completed",
  document_signed = "document_signed",
}

export interface ApiEsignEvidenceTimelineEntry {
  id: number;
  event_type: EsignEvidenceEventType;
  payload: Record<string, unknown>;
  datetime_created_at: Date;
  datetime_tsa_time: Date;
  has_tsa_token: boolean;
}

export interface ApiEsignSignatureValidation {
  is_valid: boolean;
  datetime_checked_at: Date;
  signer_cert_subject: string;
  signer_cert_matches_configured: boolean;
  timestamp_valid: boolean;
  datetime_timestamp_at: Date;
  document_hash_matches: boolean;
  reason: string | null;
}

export interface ApiEsignEvidenceReport {
  envelope: ApiEsignEvidenceEnvelope;
  signers: ApiEsignEvidenceSigner[];
  timeline: ApiEsignEvidenceTimelineEntry[];
  hash_chain_verification: ApiEsignLogChainVerification;
  signature_validation: ApiEsignSignatureValidation;
}
